import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { type ClientLicense, useUpdateClientLicense } from '@/domain/licenses';

interface EditLicenseProps {
    onClose: () => void;
    selectedLicense: ClientLicense;
}

function EditLicense({ onClose, selectedLicense }: EditLicenseProps) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const updateClientLicense = useUpdateClientLicense();

    const [licenseNumber, setLicenseNumber] = useState(selectedLicense?.numLicenses);

    return (
        <ModalComponent
            isOpen={true}
            onClose={onClose}
            centered={false}
            title={t('internal.client.updateClientLicense')}>
            <ModalBody>
                <FormField label={t('internal.client.licenseName')} htmlFor="licenseName">
                    <TextField
                        type="text"
                        id="licenseName"
                        disabled={true}
                        value={selectedLicense?.name ?? ''}
                    />
                </FormField>
                <FormField
                    label={t('internal.clientLicense.numberOfLicenses')}
                    htmlFor="licenseNumber">
                    <TextField
                        type="text"
                        id="licenseNumber"
                        placeholder=""
                        value={licenseNumber}
                        onChange={(e) => {
                            !Number.isNaN(Number(e.target.value)) &&
                                setLicenseNumber(Number(e.target.value));
                        }}
                    />
                </FormField>
            </ModalBody>
            <ModalFooter>
                <Button variant="secondary" onClick={onClose}>
                    {t('common.actions.cancel')}
                </Button>
                <Button
                    variant="primary"
                    onClick={async (event) => {
                        event.preventDefault();
                        try {
                            await updateClientLicense.mutateAsync({
                                id: selectedLicense.id,
                                numLicenses: licenseNumber,
                            });
                            onClose();
                        } catch (error: unknown) {
                            ToastError(
                                apiErrorContent(error, t('internal.clientLicense.updateFailed')),
                                'client-license-update-error'
                            );
                        } finally {
                            queryClient.invalidateQueries();
                        }
                    }}>
                    {t('common.actions.update')}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}

export default EditLicense;
